import {isFeatureEnabled} from 'feature-flags';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {useGetDirectSaleQuery} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import {
  ActivitySidebarAction,
  ActivitySidebarV2,
  CommunicationThreadTab,
} from '@dms/features/activity-panel';
import {buildCustomNoticesSidebarAction} from '@dms/features/custom-notices';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {buildArray} from 'shared/web';

import {DirectSaleActivityTab} from './components/DirectSaleActivityTab';
import {DirectSaleCommentsTab} from './components/DirectSaleCommentsTab';

interface DirectSaleActivityPanelProps {
  directSaleId: string;
}

export function DirectSaleActivityPanel(props: DirectSaleActivityPanelProps) {
  const {data: directSale} = useGetDirectSaleQuery({directSaleId: props.directSaleId});
  const [canReadDirectSale, canReadActivity, canReadComments, canAddComment, canDeleteComment] =
    usePermissions({
      permissionKeys: [
        'directSaleRead',
        'directSaleActivityRead',
        'directSaleCommentRead',
        'directSaleCommentAdd',
        'directSaleCommentDelete',
      ],
    });

  const actions = buildArray<ActivitySidebarAction>()
    .when(canReadActivity, () => ({
      id: 'activity',
      label: i18n.t('entity.activityPanel.activity.title'),
      icon: 'sidebar/activity',
      component: (
        <DirectSaleActivityTab
          directSaleId={props.directSaleId}
          customerId={directSale?.customerId}
        />
      ),
    }))
    .when(
      canReadDirectSale &&
        isFeatureEnabled(featureFlags.MESSAGE_SYNCHRONIZATION) &&
        isNotNilOrEmpty(directSale?.customerId),
      () => ({
        id: 'communication',
        label: i18n.t('entity.activityPanel.communicationThread.title'),
        icon: 'communication/thread',
        component: (
          <CommunicationThreadTab
            key={directSale?.customerId}
            customerId={directSale?.customerId ?? ''}
            recordId={props.directSaleId}
            resourceId={EntityResourceIds.directSale}
          />
        ),
        disablePadding: true,
      })
    )
    .when(canReadComments, () => ({
      id: 'comments',
      label: i18n.t('entity.activityPanel.comments.title'),
      icon: 'communication/comment',
      component: (
        <DirectSaleCommentsTab
          directSaleId={props.directSaleId}
          canAddComment={canAddComment}
          canDeleteComment={canDeleteComment}
        />
      ),
    }))
    .whenFeatureEnabled(
      featureFlags.CUSTOM_NOTICES,
      buildCustomNoticesSidebarAction({
        resourceId: EntityResourceIds.directSale,
        recordId: props.directSaleId,
      })
    );

  return <ActivitySidebarV2 actions={actions} />;
}
